import SidePanel from 'components/Basic/SidePanel';
import ConfirmationModal from 'components/Basic/Modal/Confirmation';
import Button from 'components/Basic/Button';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { setDeleted, setSelectedShapeId, Tree, TreeDiff, updateTree } from 'store/slices/TreeSlice';
import TreeCrownDiameterField from './TreeCrownDiameterField';
import TreeCrownHeightField from './TreeCrownHeightField';
import TreeTrunkDiameterField from './TreeTrunkDiameterField';
import TreeHeightField from './TreeHeightField';
import TreePreview2D, { ActiveTreeField } from './TreePreview2D';
import { useEffect, useRef, useState } from 'react';
import { ShapeToggleBtn, ShapeToggleGroup, ShapeToggleLabel, ShapeToggleRow, TreeFieldGroup, TreePanelContent } from './styles';
import { selectTreeDoneLoading, selectTreeState, selectTreeEditingMode, selectIsDrawingTree } from 'store/slices/TreeSlice/selector';
import useScreenDims from 'hooks/useScreenDims';
import { RIGHT_PANEL_MOBILE_BREAKPOINT } from '../../constants';
// const getTreeSnapshot = (tree: Tree) => ({
// 	id: tree.id,
// 	crownShape: tree.crownShape,
// 	crownHeight: tree.crownHeight,
// 	trunkHeight: tree.trunkHeight,
// 	trunkRadius: tree.trunkRadius,
// 	crownRadius: tree.crownRadius,
// });

export default function TreeSidePanel() {
	const dispatch = useDispatch();

	const { editModeEnabled } = useSelector((state: RootState) => state.toolNewPostions.data);
	const { selectedTreeId, selectedTree } = useSelector(selectTreeState);
	const treeEditingMode = useSelector(selectTreeEditingMode);
	const isDrawingTree = useSelector(selectIsDrawingTree);
	const isDoneLoading = useSelector(selectTreeDoneLoading);
	const [activeField, setActiveField] = useState<ActiveTreeField>(null);
	const initialTreeRef = useRef<Tree | null>(null);
	const [showConfirmClose, setShowConfirmClose] = useState(false);
	const { width: screenWidth } = useScreenDims();
	const isMobileView = screenWidth <= RIGHT_PANEL_MOBILE_BREAKPOINT;

	const hasSelection = !!selectedTree;
	const isOpen = editModeEnabled && treeEditingMode && hasSelection;
	const isPanelOpen = isOpen && !isDrawingTree;

	// const handleDone = () => {
	// 	if(!selectedTree) return;
	// 	const newTrees = trees.map(t=>{
	// 		const tcpy = {...t} as Partial<Tree>;

	// 		delete tcpy.id;
	// 		delete tcpy.trunkRadius;
	// 		if(tcpy.crownRadius){
	// 			tcpy.crownRadius *= groundMetersPerPixel;
	// 		}

	// 		return tcpy;
	// 	});

	// 	const payload = {...selectedTree} as Partial<Tree>;

	// 	delete payload.id;
	// 	delete payload.trunkRadius;

	// 	updateDsm({uuid, payload: { newTrees}});
	// };

	useEffect(() => {
		if (!selectedTreeId) {
			initialTreeRef.current = null;
			return;
		}
		if (!isOpen || !selectedTree) return;
		if (!initialTreeRef.current || initialTreeRef.current.id !== selectedTree.id) {
			initialTreeRef.current = selectedTree;
		}
	}, [isOpen, selectedTree, selectedTreeId]);

	const hasEdits = () => {
		if (!selectedTree || !initialTreeRef.current) return false;
		if (initialTreeRef.current.id !== selectedTree.id) return false;
		return TreeDiff.hasChanges(selectedTree, initialTreeRef.current);
	};

	const resetTreeEdits = () => {
		if (!selectedTreeId || !initialTreeRef.current) return;
		const original = initialTreeRef.current;
		dispatch(updateTree({
			id: selectedTreeId,
			tree: {
				crownShape: original.crownShape,
				crownHeight: original.crownHeight,
				treeHeight: original.treeHeight,
				trunkRadius: original.trunkRadius,
				crownRadius: original.crownRadius,
			}
		}));
	};

	const closePanel = () => {
		dispatch(setSelectedShapeId(null));
	};

	const handleClose = () => {
		// if (hasEdits()) {
		// 	setShowConfirmClose(true);
		// 	return;
		// }
		closePanel();
	};

	const handleDelete = () => {
		if (!selectedTreeId) return;
		confirmDelete();
		// setShowConfirmDelete(true);
	};

	const confirmDelete = () => {
		if (!selectedTreeId) return;
		dispatch(setDeleted(selectedTreeId));
		dispatch(setSelectedShapeId(null));
		// setShowConfirmDelete(false);
	};

	const handleShapeChange = (shape: 'sphere' | 'cone') => {
		if (!selectedTreeId) return;
		dispatch(updateTree({
			id: selectedTreeId,
			tree: { crownShape: shape }
		}));
	};

	useEffect(() => {
		if (isOpen) {
			setActiveField(null);
		}
	}, [isOpen, selectedTreeId]);

	return (
		<>
			<SidePanel
				title="Edit Tree"
				open={isPanelOpen}
				showActionBtn={false}
				// onAction={handleDone}
				onClose={handleClose}
				closeBtnLabel="Done"
				closeBtnClassName="side-modal-close--primary"
				disableAction={!hasEdits()}
				actionLoader={isDoneLoading}
				position={isMobileView ? 'bottom' : 'right'}
				containerClassName={isMobileView ? 'side-panel-container--mobile' : undefined}
				panelClassName={isMobileView ? 'side-panel--mobile' : undefined}
				deleteBtn={
					<Button
						id="tree-sidepanel-delete"
						type="button"
						className="sidepanel-delete"
						onClick={handleDelete}
						disabled={!selectedTreeId || isDoneLoading}
					>
						Delete
					</Button>
				}
			>
				{selectedTree ? (
					<TreePanelContent key={selectedTree.id}>
						<TreeFieldGroup className="tree-field-group--grid">
							<ShapeToggleRow>
								<ShapeToggleLabel>Type</ShapeToggleLabel>
								<ShapeToggleGroup>
									<ShapeToggleBtn
										type="button"
										$active={selectedTree.crownShape !== 'cone'}
										onClick={() => !isDoneLoading && handleShapeChange('sphere')}
										aria-label="Sphere crown"
										title="Sphere"
										disabled={isDoneLoading}
									>
										<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
											<g clipPath="url(#clip0_3909_10)">
												<path d="M11.9883 23.0168V8.71484" stroke="var(--defaultIconColor)"
													strokeWidth="1.37169" strokeLinecap="round" strokeLinejoin="round"/>
												<path d="M11.9883 23.0168V8.71484" stroke="var(--defaultIconColor)"
													strokeWidth="1.37169" strokeLinecap="round" strokeLinejoin="round"/>
												<path d="M8.69679 17.9808C6.80791 17.2133 5.24405 15.8121 4.27284 
												14.0171C3.30163 12.222 2.98345 10.1447 3.37275 8.14057C3.76205 6.13646 
												4.83463 4.33015 6.40693 3.03078C7.97923 1.7314 9.95351 1.01974 11.9919 
												1.01758C14.0324 1.01581 16.01 1.72541 17.5852 3.02463C19.1604 4.32385 
												20.235 6.13168 20.6247 8.13791C21.0144 10.1441 20.6949 12.2237 19.7209 
												14.0197C18.747 15.8157 17.1793 17.2162 15.287 17.9808"
												stroke="var(--defaultIconColor)" strokeWidth="1.37169" strokeLinecap="round"/>
												<path d="M8.69679 17.9808C6.80791 17.2133 5.24405 15.8121 4.27284 14.0171C3.30163 
											12.222 2.98345 10.1447 3.37275 8.14057C3.76205 6.13646 4.83463 4.33015 6.40693 
											3.03078C7.97923 1.7314 9.95351 1.01974 11.9919 1.01758C14.0324 1.01581 16.01 
											1.72541 17.5852 3.02463C19.1604 4.32385 20.235 6.13168 20.6247 8.13791C21.0144 
											10.1441 20.6949 12.2237 19.7209 14.0197C18.747 15.8157 17.1793 17.2162 15.287 
											17.9808" stroke="var(--defaultIconColor)" strokeWidth="1.37169" strokeLinecap="round"/>
												<path d="M11.992 15.3159L14.7379 12.5655M11.992 13.1156L9.24609 10.3652M9.79528 
											23.017H14.1888" stroke="var(--defaultIconColor)"
												strokeWidth="1.37169" strokeLinecap="round" strokeLinejoin="round"/>
												<path d="M11.992 15.3159L14.7379 12.5655M11.992 13.1156L9.24609 10.3652M9.79528 
											23.017H14.1888" stroke="var(--defaultIconColor)" strokeWidth="1.37169"
												strokeLinecap="round" strokeLinejoin="round"/>
											</g>
											<defs>
												<clipPath id="clip0_3909_10">
													<rect width="24" height="24" fill="white"/>
												</clipPath>
											</defs>
										</svg>
									</ShapeToggleBtn>
									<ShapeToggleBtn
										type="button"
										$active={selectedTree.crownShape === 'cone'}
										onClick={() => !isDoneLoading && handleShapeChange('cone')}
										aria-label="Cone crown"
										title="Cone"
										disabled={isDoneLoading}
									>
										<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
											<g clipPath="url(#clip0_3909_12)">
												<path d="M12.0023 18.1131H20.5631L15.0597 10.1686H18.7287L12.0023 1.00195L5.27588 
											10.1686H8.94482L3.44141 18.1131H12.0023ZM12.0023 18.1131V23.002" stroke="var(--defaultIconColor)"
												strokeWidth="1.37" strokeLinecap="round" strokeLinejoin="round"/>
												<path d="M12.0023 18.1131H20.5631L15.0597 10.1686H18.7287L12.0023 1.00195L5.27588 
											10.1686H8.94482L3.44141 18.1131H12.0023ZM12.0023 18.1131V23.002" stroke="var(--defaultIconColor)"
												strokeWidth="1.37" strokeLinecap="round" strokeLinejoin="round"/>
											</g>
											<defs>
												<clipPath id="clip0_3909_12">
													<rect width="24" height="24" fill="white"/>
												</clipPath>
											</defs>
										</svg>
									</ShapeToggleBtn>
								</ShapeToggleGroup>
							</ShapeToggleRow>
							<TreeHeightField
								isActive={activeField === 'treeHeight'}
								onActivate={() => setActiveField('treeHeight')}
								disabled={isDoneLoading}
							/>
							<TreeCrownDiameterField
								isActive={activeField === 'crownDiameter'}
								onActivate={() => setActiveField('crownDiameter')}
								disabled={isDoneLoading}
							/>
							<TreeTrunkDiameterField
								isActive={activeField === 'trunkDiameter'}
								onActivate={() => setActiveField('trunkDiameter')}
								disabled={isDoneLoading}
							/>
							<TreeCrownHeightField
								isActive={activeField === 'crownHeight'}
								onActivate={() => setActiveField('crownHeight')}
								disabled={isDoneLoading}
							/>
						</TreeFieldGroup>
						<TreePreview2D tree={selectedTree} activeField={activeField} />
					</TreePanelContent>
				) : null}
			</SidePanel>
			{/* <ConfirmationModal
				open={showConfirmDelete}
				message="Are you sure you want to delete this tree?"
				onConfirm={confirmDelete}
				onCancel={() => setShowConfirmDelete(false)}
			/> */}
		</>
	);
}